import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { GerenciadorConfiguracoes } from './GerenciadorConfiguracoes';
import { GerenciadorAlunosFixos } from './GerenciadorAlunosFixos';
import { GerenciadorRegras } from './GerenciadorRegras';
import { GerenciadorVisualizacaoAgenda } from './GerenciadorVisualizacaoAgenda';
import { GerenciadorUsuarios } from './GerenciadorUsuarios';
import { AgendadorPublicacao } from './AgendadorPublicacao';
import { GestaoFinanceira } from './GestaoFinanceira';
import { GerenciadorAgendaGoogle } from './GerenciadorAgendaGoogle';
import { GerenciadorPersonalizacao } from './GerenciadorPersonalizacao';
import { GerenciadorSistema } from './GerenciadorSistema';
import { GerenciadorBot } from './GerenciadorBot';
import { GerenciadorIntegracoes } from './GerenciadorIntegracoes';
import { ControleFrequencia } from './ControleFrequencia';
import { AcompanhamentoAlunosFixos } from './AcompanhamentoAlunosFixos';
import { ModalEditarVaga } from '../common/ModalEditarVaga';
import { formatDate, formatDateShort } from '../../utils/formatters';
import { useMobile } from '../../hooks/useMobile';

// Componente Toast moderno
const Toast = ({ type, message, onClose }) => {
  const icons = {
    success: '✅',
    error: '❌',
    info: 'ℹ️',
    warning: '⚠️'
  };
  
  const colors = {
    success: '#10b981',
    error: '#ef4444',
    info: '#3b82f6',
    warning: '#f59e0b'
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '16px 24px',
      borderRadius: '12px',
      fontWeight: 500,
      zIndex: 1000,
      animation: 'slideIn 0.3s ease, fadeOut 0.3s ease 4.7s',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backgroundColor: 'white',
      color: '#0f172a',
      borderLeft: `4px solid ${colors[type] || colors.info}`
    }}>
      <span style={{ fontSize: '20px' }}>{icons[type] || icons.info}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          borderRadius: '6px',
          color: '#94a3b8',
          fontSize: '20px'
        }}
      >
        ✕
      </button>
    </div>
  );
};

// Modal para Publicar com Datas
const ModalPublicarDatas = ({ isOpen, onClose, onConfirm, loading, isMobile }) => {
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    // Definir datas padrão (semana atual)
    if (isOpen) {
      const hoje = new Date();
      const inicio = new Date(hoje);
      inicio.setDate(hoje.getDate() - hoje.getDay() + 1); // Segunda-feira
      inicio.setHours(0, 0, 0, 0);
      
      const fim = new Date(inicio);
      fim.setDate(inicio.getDate() + 6); // Domingo
      fim.setHours(23, 59, 59, 999);
      
      setDataInicio(inicio.toISOString().split('T')[0]);
      setDataFim(fim.toISOString().split('T')[0]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: isMobile ? '16px' : '0'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '30px',
        width: isMobile ? '100%' : '500px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
      }}>
        <h3 style={{
          fontSize: isMobile ? '18px' : '24px',
          fontWeight: '700',
          color: '#0f172a',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>📅</span>
          Publicar Agendas com Datas Específicas
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#64748b', marginBottom: '20px' }}>
            Escolha o período de datas para publicar as agendas. Esta ação tornará as vagas visíveis para os alunos.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '15px',
            marginBottom: '25px'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#334155',
                fontSize: '14px'
              }}>
                Data Inicial
              </label>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
              />
            </div>
            
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#334155',
                fontSize: '14px'
              }}>
                Data Final
              </label>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
              />
            </div>
          </div>
          
          <div style={{
            backgroundColor: '#f8fafc',
            padding: '15px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            marginBottom: '20px'
          }}>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>ℹ️</span>
              As agendas serão publicadas de <strong>{dataInicio}</strong> até <strong>{dataFim}</strong>
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '12px 24px',
              border: '2px solid #cbd5e1',
              borderRadius: '10px',
              backgroundColor: 'transparent',
              color: '#64748b',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}
          >
            Cancelar
          </button>
          
          <button
            onClick={() => onConfirm(dataInicio, dataFim)}
            disabled={loading || !dataInicio || !dataFim}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderRadius: '10px',
              backgroundColor: loading ? '#94a3b8' : '#10b981',
              color: 'white',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {loading ? (
              <>
                <span>⏳</span>
                Publicando...
              </>
            ) : (
              <>
                <span>📤</span>
                Publicar Agendas
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal para Gerar Agenda Semanal
const ModalGerarAgenda = ({ isOpen, onClose, onConfirm, loading, isMobile, createApi }) => {
  const [dataInicio, setDataInicio] = useState('');
  const [configuracoes, setConfiguracoes] = useState([]);
  const [configuracaoId, setConfiguracaoId] = useState('');
  const [aplicarAlunosFixos, setAplicarAlunosFixos] = useState(true);
  const [carregandoConfiguracoes, setCarregandoConfiguracoes] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Definir data padrão (próxima segunda-feira)
      const hoje = new Date();
      const inicio = new Date(hoje);
      inicio.setDate(hoje.getDate() + (8 - hoje.getDay()) % 7); // Próxima segunda-feira
      inicio.setHours(0, 0, 0, 0);
      
      setDataInicio(inicio.toISOString().split('T')[0]);
      
      // Carregar configurações
      carregarConfiguracoes();
    }
  }, [isOpen]);

  const carregarConfiguracoes = async () => {
    setCarregandoConfiguracoes(true);
    try {
      const api = createApi();
      const response = await api.get('/admin/configuracoes-semana');
      setConfiguracoes(response.data);
      
      // Selecionar a primeira configuração por padrão
      if (response.data.length > 0) {
        setConfiguracaoId(response.data[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    } finally {
      setCarregandoConfiguracoes(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: isMobile ? '16px' : '0'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '30px',
        width: isMobile ? '100%' : '500px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
      }}>
        <h3 style={{
          fontSize: isMobile ? '18px' : '24px',
          fontWeight: '700',
          color: '#0f172a',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>📋</span>
          Gerar Nova Agenda Semanal
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#64748b', marginBottom: '20px' }}>
            Crie uma nova semana de vagas com base em uma configuração pré-definida.
          </p>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#334155',
              fontSize: '14px'
            }}>
              Configuração da Semana
            </label>
            {carregandoConfiguracoes ? (
              <div style={{
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '10px',
                textAlign: 'center',
                color: '#64748b'
              }}>
                Carregando configurações...
              </div>
            ) : configuracoes.length === 0 ? (
              <div style={{
                padding: '12px',
                backgroundColor: '#fef3c7',
                borderRadius: '10px',
                border: '1px solid #f59e0b',
                color: '#92400e'
              }}>
                ⚠️ Nenhuma configuração encontrada. Crie uma configuração primeiro.
              </div>
            ) : (
              <select
                value={configuracaoId}
                onChange={(e) => setConfiguracaoId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: '16px',
                  outline: 'none',
                  backgroundColor: 'white'
                }}
              >
                {configuracoes.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.nome} {config.ativa ? '(Ativa)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#334155',
              fontSize: '14px'
            }}>
              Data de Início da Semana
            </label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
            />
            <p style={{
              marginTop: '4px',
              fontSize: '12px',
              color: '#64748b'
            }}>
              Recomendado: Segunda-feira da semana desejada
            </p>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '20px',
            padding: '12px',
            backgroundColor: '#f8fafc',
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            <input
              type="checkbox"
              id="aplicarAlunosFixos"
              checked={aplicarAlunosFixos}
              onChange={(e) => setAplicarAlunosFixos(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer'
              }}
            />
            <label
              htmlFor="aplicarAlunosFixos"
              style={{
                cursor: 'pointer',
                color: '#334155',
                fontWeight: '500'
              }}
            >
              Aplicar alunos fixos automaticamente
            </label>
          </div>
          
          <div style={{
            backgroundColor: '#f0f9ff',
            padding: '15px',
            borderRadius: '10px',
            border: '1px solid #bae6fd',
            marginBottom: '20px'
          }}>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#0369a1',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <span>ℹ️</span>
              <div>
                <strong>Importante:</strong> Esta ação criará uma nova semana de vagas em modo rascunho. 
                Você precisará publicá-las posteriormente para torná-las visíveis aos alunos.
                {configuracoes.find(c => c.id === configuracaoId)?.descricao && (
                  <div style={{ marginTop: '8px' }}>
                    <strong>Descrição:</strong> {configuracoes.find(c => c.id === configuracaoId)?.descricao}
                  </div>
                )}
              </div>
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '12px 24px',
              border: '2px solid #cbd5e1',
              borderRadius: '10px',
              backgroundColor: 'transparent',
              color: '#64748b',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}
          >
            Cancelar
          </button>
          
          <button
            onClick={() => onConfirm(configuracaoId, dataInicio, aplicarAlunosFixos)}
            disabled={loading || !configuracaoId || !dataInicio || configuracoes.length === 0}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderRadius: '10px',
              backgroundColor: loading ? '#94a3b8' : '#8b5cf6',
              color: 'white',
              fontWeight: '600',
              cursor: (loading || !configuracaoId || !dataInicio || configuracoes.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {loading ? (
              <>
                <span>⏳</span>
                Gerando...
              </>
            ) : (
              <>
                <span>✨</span>
                Gerar Agenda
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Ícones
const Icons = {
  Calendar: '📅',
  Settings: '⚙️',
  Users: '👥',
  UserCog: '👤',
  Clock: '🕒',
  Filter: '🔍',
  ChevronLeft: '◀',
  ChevronRight: '▶',
  Zap: '⚡',
  RefreshCw: '🔄',
  Trash2: '🗑️',
  BarChart3: '📊',
  Bug: '🐛',
  Edit3: '✏️',
  Lock: '🔒',
  Unlock: '🔓',
  DoorOpen: '🚪',
  Search: '🔍',
  CheckCircle: '✅',
  AlertCircle: '⚠️',
  Info: 'ℹ️',
  Bell: '🔔',
  Download: '📥',
  Upload: '📤',
  Plus: '➕',
  X: '✕',
  MoreVertical: '⋮',
  Clock2: '⏰',
  Eye: '👁️',
  Check: '✓',
  Ban: '🚫',
  Refresh: '🔄',
  Star: '⭐',
  Book: '📚',
  Shield: '🛡️',
  Users2: '👥',
  CalendarClock: '🗓️',
  CalendarCheck: '📆',
  CalendarX: '🗑️',
  CalendarRange: '📅',
  CalendarPlus: '📅➕',
  Sparkles: '✨'
};

// Funções auxiliares
const formatarDataPTBR = (date) => {
  const diasSemana = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  const diasSemanaAbrev = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const d = new Date(date);
  return {
    diaSemana: diasSemana[d.getDay()],
    diaSemanaAbrev: diasSemanaAbrev[d.getDay()],
    dataFormatada: `${d.getDate()}/${d.getMonth() + 1}`,
    diaNumero: d.getDate(),
    mesNumero: d.getMonth() + 1,
    ano: d.getFullYear(),
    dataCompleta: `${diasSemana[d.getDay()]}, ${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`
  };
};

const formatarHora = (date) => {
  const d = new Date(date);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

// Função para formatar período semanal
const formatarPeriodoSemanal = (inicio, fim) => {
  const inicioFormatado = formatarDataPTBR(inicio);
  const fimFormatado = formatarDataPTBR(fim);
  return `${inicioFormatado.diaSemanaAbrev.toLowerCase()}, ${inicioFormatado.dataFormatada} - ${fimFormatado.diaSemanaAbrev.toLowerCase()}, ${fimFormatado.dataFormatada}`;
};

// Helper function para determinar status da vaga
const getStatusInfo = (vaga, vagasAtualizadasLocalmente) => {
  // Verifica se há atualizações locais
  const vagaAtualizada = vagasAtualizadasLocalmente[vaga.id];
  const isTrancada = vagaAtualizada?.trancada !== undefined ?
    vagaAtualizada.trancada : vaga.trancada;
  const isRascunho = vagaAtualizada?.rascunho !== undefined ?
    vagaAtualizada.rascunho : vaga.rascunho;
  const hasAgendamento = vagaAtualizada?.agendamento_id !== undefined ?
    vagaAtualizada.agendamento_id : vaga.agendamento_id;
  const isFixo = vagaAtualizada?.aluno_fixo_id !== undefined ?
    vagaAtualizada.aluno_fixo_id : vaga.aluno_fixo_id;

  if (isTrancada && isFixo) return {
    text: 'Fixo (Ocupada)',
    cls: 'trancada',
    status: 'fixo-trancada',
    icon: Icons.Lock,
    acoesPermitidas: ['editar', 'liberar', 'destrancar'],
    cor: '#8b5cf6'
  };

  if (isTrancada) return {
    text: 'Trancada',
    cls: 'trancada',
    status: 'trancada',
    icon: Icons.Lock,
    acoesPermitidas: ['editar', 'destrancar'],
    cor: '#f59e0b'
  };

  // Rascunho com aluno fixo → aparece como trancado (slot reservado)
  if (isRascunho && isFixo) return {
    text: 'Fixo',
    cls: 'trancada',
    status: 'trancada',
    icon: Icons.Lock,
    acoesPermitidas: ['editar', 'destrancar'],
    cor: '#8b5cf6'
  };

  if (isRascunho) return {
    text: 'Rascunho',
    cls: 'rascunho',
    status: 'rascunho',
    icon: Icons.Edit3,
    acoesPermitidas: ['editar', 'trancar'],
    cor: '#3b82f6'
  };
  
  if (hasAgendamento) return {
    text: 'Ocupada',
    cls: 'ocupada',
    status: 'ocupada',
    icon: Icons.Users,
    acoesPermitidas: ['editar', 'liberar', 'trancar'],
    cor: '#ef4444'
  };

  if (isFixo) return {
    text: 'Fixo',
    cls: 'ocupada',
    status: 'fixo',
    icon: Icons.Users,
    acoesPermitidas: ['editar', 'liberar', 'trancar'],
    cor: '#8b5cf6'
  };

  return {
    text: 'Disponível',
    cls: 'disponivel',
    status: 'disponivel',
    icon: Icons.CheckCircle,
    acoesPermitidas: ['editar', 'trancar'],
    cor: '#10b981'
  };
};

export const PainelAdmin = ({
  grade,
  user,
  createApi,
  onRefresh,
  periodo,
  dataReferencia,
  setDataReferencia
}) => {
  const [abaAtiva, setAbaAtiva] = useState('vendas');
  const [subAbaConfiguracoes, setSubAbaConfiguracoes] = useState('semana');
  const [subAbaSistema, setSubAbaSistema] = useState('personalizar');
  const [subAbaAlunos, setSubAbaAlunos] = useState('usuarios');
  const [subAbaPublicacao, setSubAbaPublicacao] = useState('publicacao');
  const [modalEditarVaga, setModalEditarVaga] = useState(null);
  const [statusAtualizarVagas, setStatusAtualizarVagas] = useState('idle'); // idle | atualizando | ok
  const [loading, setLoading] = useState(false);
  const [alunos, setAlunos] = useState([]);
  const [buscaAluno, setBuscaAluno] = useState('');
  const [notificacao, setNotificacao] = useState({ tipo: '', mensagem: '' });
  // Armazena a chave 'YYYY-MM-DD' do dia selecionado (derivamos o objeto via useMemo)
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [selectedHorario, setSelectedHorario] = useState(null);
  
  const [vagasAtualizadasLocalmente, setVagasAtualizadasLocalmente] = useState({});
  const [botoesAtualizando, setBotoesAtualizando] = useState({});
  const [statsApi, setStatsApi] = useState(null);
  const [filasPorVaga, setFilasPorVaga] = useState({});
  const [filasCarregando, setFilasCarregando] = useState({});
  
  // ESTADOS PARA OS BOTÕES DE AÇÕES RÁPIDAS
  const [loadingPublicar, setLoadingPublicar] = useState(false);
  const [loadingLimpar, setLoadingLimpar] = useState(false);
  const [loadingPublicarDatas, setLoadingPublicarDatas] = useState(false);
  const [loadingGerarAgenda, setLoadingGerarAgenda] = useState(false);
  const [mostrarModalPublicarDatas, setMostrarModalPublicarDatas] = useState(false);
  const [mostrarModalGerarAgenda, setMostrarModalGerarAgenda] = useState(false);

  const isMobile = useMobile();

  // Mapeamento de abas com ícones
  const abasBase = [
    { id: 'vendas',       label: 'Vagas',        icon: Icons.Calendar,     roles: ['admin','professor'] },
    { id: 'frequencia',   label: 'Agenda',       icon: '📋',               roles: ['admin','professor'] },
    { id: 'configuracoes',label: 'Configurações', icon: Icons.Settings,     roles: ['admin','professor'] },
    { id: 'alunos',       label: 'Alunos',        icon: Icons.Users2,       roles: ['admin','professor'] },
    { id: 'publicacao',   label: 'Publicação',    icon: Icons.CalendarClock,roles: ['admin','professor'] },
    { id: 'financeiro',    label: 'Financeiro',     icon: '💰',              roles: ['admin'] },
    { id: 'sistema',       label: 'Sistema',        icon: '⚙️',              roles: ['admin'] },
  ];
  const papel = user?.is_admin ? 'admin' : user?.is_professor ? 'professor' : null;
  const abas = abasBase.filter(a => a.roles.includes(papel));

  // Funções para navegação entre semanas
  // IMPORTANTE: não chamar onRefresh() junto com setDataReferencia() — isso dispara
  // dois fetches simultâneos, o primeiro com closure stale (semana errada).
  // O useEffect do useGradeSemanal já recarrega quando dataReferencia muda.
  const handleSemanaAnterior = () => {
    const base = dataReferencia ? new Date(dataReferencia + 'T12:00:00') : new Date();
    base.setDate(base.getDate() - 7);
    setDataReferencia(base.toISOString().split('T')[0]);
  };

  const handleSemanaProxima = () => {
    const base = dataReferencia ? new Date(dataReferencia + 'T12:00:00') : new Date();
    base.setDate(base.getDate() + 7);
    setDataReferencia(base.toISOString().split('T')[0]);
  };

  const handleHoje = () => {
    if (dataReferencia === '') {
      // Já está na semana atual — forçar reload explícito (setDataReferencia('') seria no-op)
      if (onRefresh) onRefresh();
    } else {
      setDataReferencia('');
      // useEffect cuida do reload
    }
  };

  // Helper: gera chave 'YYYY-MM-DD' a partir de um objeto dia
  const getDiaKey = useCallback((dia) =>
    `${dia.data.getFullYear()}-${String(dia.data.getMonth()+1).padStart(2,'0')}-${String(dia.data.getDate()).padStart(2,'0')}`,
  []);

  const agruparPorDia = useCallback((vagas) => {
    if (!vagas || vagas.length === 0) return [];
    const grupos = {};

    vagas.forEach(vagaOriginal => {
      const vagaAtualizada = {
        ...vagaOriginal,
        ...vagasAtualizadasLocalmente[vagaOriginal.id]
      };

      const data = new Date(vagaAtualizada.data_hora);
      const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;

      if (!grupos[chave]) {
        grupos[chave] = { data, ...formatarDataPTBR(data), vagas: [] };
      }

      grupos[chave].vagas.push(vagaAtualizada);
    });

    return Object.keys(grupos)
      .sort()
      .map(chave => ({
        ...grupos[chave],
        vagas: grupos[chave].vagas.sort((a, b) => {
          const diff = new Date(a.data_hora) - new Date(b.data_hora);
          return diff !== 0 ? diff : (parseInt(a.id) || 0) - (parseInt(b.id) || 0);
        })
      }));
  }, [vagasAtualizadasLocalmente]);

  const dias = agruparPorDia(grade || []);

  // selectedDay é sempre derivado de dias via chave — nunca fica stale
  const selectedDay = useMemo(
    () => (selectedDayKey ? dias.find(d => getDiaKey(d) === selectedDayKey) ?? null : null),
    [dias, selectedDayKey, getDiaKey]
  );

  // Resetar ao mudar de semana (compara por valor, não por referência de objeto)
  useEffect(() => {
    setSelectedDayKey(null);
    setSelectedHorario(null);
    setStatsApi(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo?.inicio?.getTime()]);

  // Selecionar dia automaticamente quando a semana carrega
  useEffect(() => {
    if (dias.length > 0 && !selectedDayKey) {
      const hoje = new Date();
      const diaHoje = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}-${String(hoje.getDate()).padStart(2,'0')}`;
      const diaAlvo = dias.find(d => getDiaKey(d) === diaHoje)
        || dias.find(d => d.vagas.length > 0)
        || dias[0];
      setSelectedDayKey(getDiaKey(diaAlvo));
    }
  }, [dias, selectedDayKey, getDiaKey]);

  const horariosDoDia = selectedDay
    ? [...new Set(selectedDay.vagas.map(v => formatarHora(v.data_hora)))].sort()
    : [];

  const vagasDoHorario = selectedDay && selectedHorario
    ? selectedDay.vagas.filter(v => formatarHora(v.data_hora) === selectedHorario)
    : selectedDay
    ? selectedDay.vagas
    : [];

  // Extrai 'YYYY-MM-DD' usando fuso LOCAL (evita shift UTC ao usar toISOString())
  const localDateStr = useCallback((d) => {
    const date = d instanceof Date ? d : new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
  }, []);

  // Carrega stats direto do backend para evitar problemas de cache/estado local
  // periodo.fim é a segunda da semana SEGUINTE (limite exclusivo) — subtrai 1 dia para
  // enviar o sábado (inclusive) que o backend espera antes de adicionar T23:59:59.
  const refetchStats = useCallback(() => {
    if (!periodo || !createApi) return;
    const api = createApi();
    const inicio = localDateStr(periodo.inicio);
    const fimExclusivo = periodo.fim instanceof Date ? new Date(periodo.fim) : new Date(periodo.fim);
    fimExclusivo.setDate(fimExclusivo.getDate() - 1); // segunda → sábado (inclusive)
    const fim = localDateStr(fimExclusivo);
    api.get(`/admin/stats-semana?inicio=${inicio}&fim=${fim}`)
      .then(r => setStatsApi(r.data))
      .catch(() => {});
  }, [periodo, createApi, localDateStr]);

  useEffect(() => {
    refetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, grade]);

  const estatisticas = (() => {
    const agora = new Date();
    if (!grade || grade.length === 0)
      return { total: 0, fixas: 0, trancadas: 0, ocupadas: 0, rascunhos: 0, livres: 0 };
    const fixas     = grade.filter(v => v.aluno_fixo_id).length;
    const trancadas = grade.filter(v => v.trancada && !v.aluno_fixo_id).length;
    const rascunhos = grade.filter(v => v.rascunho).length;
    const ocupadas  = statsApi?.ocupadas ?? grade.filter(v => v.agendamento_id && !v.trancada).length;
    const livres    = grade.filter(v => !v.aluno_fixo_id && !v.trancada && !v.agendamento_id && !v.rascunho).length;
    return {
      total:    statsApi?.total    ?? grade.length,
      fixas:    statsApi?.fixas    ?? fixas,
      trancadas: statsApi?.trancadas ?? trancadas,
      rascunhos,
      ocupadas,
      livres,
    };
  })();

  const showNotificacao = useCallback((tipo, mensagem) => {
    setNotificacao({ tipo, mensagem });
    setTimeout(() => setNotificacao({ tipo: '', mensagem: '' }), 5000);
  }, []);

  // Função para atualizar aparência localmente
  const atualizarAparenciaVaga = useCallback((vagaId, novosAtributos) => {
    setVagasAtualizadasLocalmente(prev => ({
      ...prev,
      [vagaId]: { ...prev[vagaId], ...novosAtributos }
    }));
  }, []);

  const limparBotao = useCallback((vagaId) => {
    setBotoesAtualizando(prev => {
      const s = { ...prev };
      delete s[vagaId];
      return s;
    });
  }, []);

  // AÇÃO SIMPLIFICADA: Trancar vaga (SEM CONFIRMAÇÃO)
  const handleTrancarVaga = useCallback(async (vaga) => {
    const vagaId = vaga.id;
    setBotoesAtualizando(prev => ({ ...prev, [vagaId]: 'trancar' }));
    atualizarAparenciaVaga(vagaId, { trancada: true });

    try {
      const api = createApi();
      await api.post(`/admin/vaga/${vagaId}/trancar`);
      showNotificacao('success', 'Vaga trancada com sucesso!');
      refetchStats();
    } catch (err) {
      atualizarAparenciaVaga(vagaId, {
        trancada: false,
        status: vaga.rascunho ? 'rascunho' : (vaga.agendamento_id ? 'ocupada' : 'disponivel')
      });
      showNotificacao('error', err.response?.data?.error || 'Erro ao trancar vaga');
    } finally {
      limparBotao(vagaId);
    }
  }, [atualizarAparenciaVaga, createApi, refetchStats, showNotificacao, limparBotao]);

  // AÇÃO SIMPLIFICADA: Destrancar vaga (SEM CONFIRMAÇÃO)
  const handleDestrancarVaga = useCallback(async (vaga) => {
    const vagaId = vaga.id;
    setBotoesAtualizando(prev => ({ ...prev, [vagaId]: 'destrancar' }));
    atualizarAparenciaVaga(vagaId, {
      trancada: false,
      aluno_fixo_id: null,
      aluno_fixo_nome: null,
    });

    try {
      const api = createApi();
      await api.post(`/admin/vaga/${vagaId}/destrancar`);
      showNotificacao('success', 'Vaga destrancada com sucesso!');
      refetchStats();
    } catch (err) {
      atualizarAparenciaVaga(vagaId, { trancada: true });
      showNotificacao('error', err.response?.data?.error || 'Erro ao destrancar vaga');
    } finally {
      limparBotao(vagaId);
    }
  }, [atualizarAparenciaVaga, createApi, refetchStats, showNotificacao, limparBotao]);

  // AÇÃO SIMPLIFICADA: Liberar vaga (SEM CONFIRMAÇÃO)
  const handleLiberarVaga = useCallback(async (vaga) => {
    const vagaId = vaga.id;
    setBotoesAtualizando(prev => ({ ...prev, [vagaId]: 'liberar' }));
    atualizarAparenciaVaga(vagaId, {
      agendamento_id: null,
      aluno_nome: null,
      aluno_id: null,
      aluno_fixo_id: null,
      aluno_fixo_nome: null,
    });

    try {
      const api = createApi();
      await api.post(`/admin/vaga/${vagaId}/liberar`);
      showNotificacao('success', 'Vaga liberada com sucesso!');
      refetchStats();
    } catch (err) {
      atualizarAparenciaVaga(vagaId, {
        agendamento_id: vaga.agendamento_id,
        aluno_nome: vaga.aluno_nome,
        aluno_id: vaga.aluno_id,
        aluno_fixo_id: vaga.aluno_fixo_id,
        aluno_fixo_nome: vaga.aluno_fixo_nome,
      });
      showNotificacao('error', err.response?.data?.error || 'Erro ao liberar vaga');
    } finally {
      limparBotao(vagaId);
    }
  }, [atualizarAparenciaVaga, createApi, refetchStats, showNotificacao, limparBotao]);

  // FUNÇÃO: Publicar Agendas da semana VISUALIZADA
  const handlePublicarAgendas = async () => {
    const rascunhosCount = grade?.filter(v => v.rascunho).length || 0;
    if (rascunhosCount === 0) {
      showNotificacao('info', 'Não há rascunhos para publicar nesta semana');
      return;
    }

    // periodo.fim é a segunda da semana seguinte (exclusivo) → subtrai 1 dia para o sábado (inclusive)
    const dataInicio = periodo?.inicio ? localDateStr(periodo.inicio) : null;
    const fimInclusivo = periodo?.fim ? new Date(periodo.fim) : null;
    if (fimInclusivo) fimInclusivo.setDate(fimInclusivo.getDate() - 1);
    const dataFim = fimInclusivo ? localDateStr(fimInclusivo) : null;

    const periodoTexto = dataInicio && dataFim
      ? ` (${dataInicio} a ${dataFim})`
      : '';

    if (!window.confirm(`Publicar ${rascunhosCount} rascunho(s)${periodoTexto}? Esta ação tornará as vagas visíveis para os alunos.`)) {
      return;
    }

    setLoadingPublicar(true);
    try {
      const api = createApi();
      let response;
      if (dataInicio && dataFim) {
        response = await api.post('/admin/publicar-com-datas', {
          data_inicio: dataInicio,
          data_fim: dataFim
        });
      } else {
        response = await api.post('/admin/publicar-semana');
      }

      showNotificacao('success', response.data.message || 'Agendas publicadas com sucesso!');

      if (onRefresh) {
        setTimeout(() => onRefresh(), 1000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erro ao publicar agendas';
      showNotificacao('error', errorMsg);
    } finally {
      setLoadingPublicar(false);
    }
  };

  // FUNÇÃO: Publicar vaga individual (rascunho → publicada)
  const handlePublicarVaga = useCallback(async (vaga) => {
    const vagaId = vaga.id;
    setBotoesAtualizando(prev => ({ ...prev, [vagaId]: 'publicar' }));
    atualizarAparenciaVaga(vagaId, { rascunho: false });

    try {
      const api = createApi();
      await api.put(`/admin/vaga/${vagaId}`, {
        descricao:     vaga.descricao,
        aluno_fixo_id: vaga.aluno_fixo_id,
        trancada:      vaga.trancada,
        rascunho:      false
      });
      showNotificacao('success', 'Vaga publicada com sucesso!');
      refetchStats();
    } catch (err) {
      atualizarAparenciaVaga(vagaId, { rascunho: true });
      showNotificacao('error', err.response?.data?.error || 'Erro ao publicar vaga');
    } finally {
      limparBotao(vagaId);
    }
  }, [atualizarAparenciaVaga, createApi, refetchStats, showNotificacao, limparBotao]);

  // FUNÇÃO: Limpar Agendas (para a semana atual ou período especificado)
  const handleLimparAgendas = async () => {
    if (!periodo || !window.confirm('ATENÇÃO: Esta ação irá limpar TODAS as agendas da semana atual, incluindo agendamentos existentes. Esta ação não pode ser desfeita. Deseja continuar?')) {
      return;
    }

    setLoadingLimpar(true);
    try {
      const api = createApi();
      // Passa strings locais — Date objects serializados via JSON viram ISO UTC e corrompem a lógica T12:00:00
      const fimInc = new Date(periodo.fim);
      fimInc.setDate(fimInc.getDate() - 1);
      const response = await api.delete('/admin/limpar-semana', {
        data: {
          data_inicio: localDateStr(periodo.inicio),
          data_fim: localDateStr(fimInc)
        }
      });
      
      showNotificacao('success', response.data.message || 'Agendas limpas com sucesso!');
      
      // Recarregar dados
      if (onRefresh) {
        setTimeout(() => onRefresh(), 1000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erro ao limpar agendas';
      showNotificacao('error', errorMsg);
    } finally {
      setLoadingLimpar(false);
    }
  };

  // FUNÇÃO: Publicar Agendas com Datas Específicas
  const handlePublicarComDatas = async (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim) {
      showNotificacao('error', 'Selecione as datas de início e fim');
      return;
    }

    setLoadingPublicarDatas(true);
    try {
      const api = createApi();
      const response = await api.post('/admin/publicar-com-datas', {
        data_inicio: dataInicio,
        data_fim: dataFim
      });
      
      showNotificacao('success', response.data.message || 'Agendas publicadas com sucesso!');
      setMostrarModalPublicarDatas(false);
      
      // Recarregar dados
      if (onRefresh) {
        setTimeout(() => onRefresh(), 1000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erro ao publicar agendas com datas específicas';
      showNotificacao('error', errorMsg);
    } finally {
      setLoadingPublicarDatas(false);
    }
  };

  // NOVA FUNÇÃO: Gerar Agenda Semanal
  const handleGerarAgenda = async (configuracaoId, dataInicio, aplicarAlunosFixos) => {
    if (!configuracaoId || !dataInicio) {
      showNotificacao('error', 'Selecione uma configuração e data de início');
      return;
    }

    setLoadingGerarAgenda(true);
    try {
      const api = createApi();
      const response = await api.post('/admin/gerar-semana-configuracao', {
        configuracao_id: configuracaoId,
        data_inicio: dataInicio,
        aplicar_alunos_fixos: aplicarAlunosFixos
      });
      
      showNotificacao('success', response.data.message || 'Agenda semanal gerada com sucesso!');
      setMostrarModalGerarAgenda(false);
      
      // Recarregar dados
      if (onRefresh) {
        setTimeout(() => onRefresh(), 1500);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erro ao gerar agenda semanal';
      showNotificacao('error', errorMsg);
    } finally {
      setLoadingGerarAgenda(false);
    }
  };

  const handleEditarVaga = async (vaga) => {
    try {
      const api = createApi();
      const response = await api.get(`/admin/vaga/${vaga.id}`);
      setModalEditarVaga(response.data);
    } catch (err) {
      showNotificacao('error', 'Erro ao carregar dados da vaga');
    }
  };

  const handleAtualizarVagas = async () => {
    if (statusAtualizarVagas === 'atualizando') return;
    setStatusAtualizarVagas('atualizando');
    try {
      await onRefresh?.();
    } finally {
      setStatusAtualizarVagas('ok');
      setTimeout(() => setStatusAtualizarVagas('idle'), 1200);
    }
  };

  const carregarFilaVaga = async (vagaId) => {
    if (filasCarregando[vagaId]) return;
    setFilasCarregando(prev => ({ ...prev, [vagaId]: true }));
    try {
      const api = createApi();
      const res = await api.get(`/admin/waitlist/${vagaId}`);
      setFilasPorVaga(prev => ({ ...prev, [vagaId]: res.data.fila || [] }));
    } catch {
      setFilasPorVaga(prev => ({ ...prev, [vagaId]: [] }));
    } finally {
      setFilasCarregando(prev => ({ ...prev, [vagaId]: false }));
    }
  };

  const removerDaFilaAdmin = async (waitlistId, vagaId) => {
    if (!window.confirm('Remover este aluno da fila de espera?')) return;
    try {
      const api = createApi();
      await api.delete(`/admin/waitlist/${waitlistId}`);
      setFilasPorVaga(prev => ({
        ...prev,
        [vagaId]: (prev[vagaId] || []).filter(e => e.id !== waitlistId)
      }));
    } catch (err) {
      alert('Erro ao remover: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSalvarEdicaoVaga = async (vagaEditada) => {
    if (!vagaEditada) return;

    setLoading(true);
    try {
      const api = createApi();
      const response = await api.put(`/admin/vaga/${vagaEditada.id}`, vagaEditada);

      // Atualiza apenas a vaga editada localmente — sem reload da grade inteira
      const vagaAtualizada = response.data?.vaga || vagaEditada;
      atualizarAparenciaVaga(vagaEditada.id, {
        descricao:       vagaAtualizada.descricao,
        trancada:        vagaAtualizada.trancada,
        rascunho:        vagaAtualizada.rascunho,
        aluno_fixo_id:   vagaAtualizada.aluno_fixo_id ?? null,
        aluno_fixo_nome: vagaAtualizada.aluno_fixo_id
          ? (buscaAluno || vagaEditada.aluno_fixo_nome || null)
          : null,
      });

      showNotificacao('success', 'Vaga atualizada com sucesso!');
      setModalEditarVaga(null);
      refetchStats();
    } catch (err) {
      showNotificacao('error', err.response?.data?.error || 'Erro ao atualizar vaga');
    } finally {
      setLoading(false);
    }
  };

  const handleLimparVagaModal = async (vagaId) => {
    try {
      const api = createApi();
      const res = await api.post(`/admin/vaga/${vagaId}/liberar`);

      showNotificacao('success', 'Vaga limpa e disponível para novos agendamentos!');
      setModalEditarVaga(null);
      atualizarAparenciaVaga(vagaId, {
        agendamento_id: null,
        aluno_fixo_id: null,
        aluno_fixo_nome: null,
        aluno_nome: null,
        aluno_id: null,
        descricao: res.data?.vaga?.descricao ?? null,
        trancada: false,
      });
      refetchStats();
    } catch (err) {
      showNotificacao('error', err.response?.data?.error || 'Erro ao limpar vaga');
      throw err;
    }
  };

  const buscarAlunos = async (query) => {
    if (query.length < 2) { setAlunos([]); return; }
    try {
      const api = createApi();
      const res = await api.get(`/admin/alunos?q=${encodeURIComponent(query)}`);
      setAlunos(res.data);
    } catch (err) {
      console.error('Erro ao buscar alunos:', err);
    }
  };

  useEffect(() => {
    if (buscaAluno) {
      const timer = setTimeout(() => buscarAlunos(buscaAluno), 300);
      return () => clearTimeout(timer);
    } else {
      setAlunos([]);
    }
  }, [buscaAluno]);

  // Mapeamento de ações para botões
  const mapAcaoParaBotao = useCallback((vaga) => ({
    editar: {
      label: 'Editar',
      icon: Icons.Edit3,
      className: 'btn-outline',
      handler: () => handleEditarVaga(vaga)
    },
    publicar: {
      label: 'Publicar',
      icon: Icons.Upload,
      className: 'btn-success',
      handler: () => handlePublicarVaga(vaga)
    },
    trancar: {
      label: 'Trancar',
      icon: Icons.Lock,
      className: 'btn-warning',
      handler: () => handleTrancarVaga(vaga)
    },
    destrancar: {
      label: 'Destrancar',
      icon: Icons.Unlock,
      className: 'btn-success',
      handler: () => handleDestrancarVaga(vaga)
    },
    liberar: {
      label: 'Liberar',
      icon: Icons.DoorOpen,
      className: 'btn-danger',
      handler: () => handleLiberarVaga(vaga)
    }
  }), [handleTrancarVaga, handleDestrancarVaga, handleLiberarVaga, handlePublicarVaga]);

  return (
    <>
      <style>
        {`
        .admin-panel {
          font-family: 'Inter', sans-serif;
          background: #f8fafc;
          min-height: 100vh;
          padding: ${isMobile ? '8px 4px' : '20px'};
          box-sizing: border-box;
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
        }

        .admin-container {
          background: white;
          border-radius: ${isMobile ? '12px' : '20px'};
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          overflow: hidden;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .admin-header {
          background: linear-gradient(135deg, #348d52 0%, #0d5724 100%);
          color: white;
          padding: ${isMobile ? '16px 12px' : '25px 30px'};
          border-radius: ${isMobile ? '12px 12px 0 0' : '20px 20px 0 0'};
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: ${isMobile ? '12px' : '20px'};
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: ${isMobile ? '12px' : '15px'};
          flex: 1;
          min-width: 0;
        }

        .header-icon {
          background: rgba(255, 255, 255, 0.2);
          padding: ${isMobile ? '10px' : '15px'};
          border-radius: 12px;
          backdrop-filter: blur(10px);
          font-size: ${isMobile ? '22px' : '28px'};
          flex-shrink: 0;
        }

        /* Título reduzido em 12% */
        .header-title {
          font-size: ${isMobile ? '15.84px' : '24.64px'}; /* 18px * 0.88 = 15.84px, 28px * 0.88 = 24.64px */
          font-weight: 700;
          letter-spacing: -0.5px;
          line-height: 1.2;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: normal;
        }

        .header-periodo {
          font-size: ${isMobile ? '12px' : '14px'};
          opacity: 0.9;
          margin-top: 8px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
        }

        .periodo-texto {
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 1;
          min-width: 0;
        }

        .navegacao-semanas {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        /* Botões de navegação ajustados - CORRIGIDO SOBREPOSIÇÃO */
        .btn-navegacao {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          padding: ${isMobile ? '6px 10px' : '8px 16px'};
          border-radius: 8px;
          font-size: ${isMobile ? '11px' : '13px'};
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${isMobile ? '4px' : '10px'};
          min-width: ${isMobile ? 'auto' : '105px'};
          white-space: nowrap;
          height: ${isMobile ? '32px' : '38px'};
          line-height: 1.2;
        }

        .btn-navegacao:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }

        .btn-navegacao:active {
          transform: translateY(0);
        }

        .btn-navegacao.hoje {
          background: rgba(255, 255, 255, 0.15);
          font-weight: 700;
          min-width: ${isMobile ? 'auto' : '70px'}; /* Hoje precisa de menos espaço */
        }

        .btn-navegacao:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        /* Ícones nos botões ajustados */
        .btn-navegacao span {
          display: inline-block;
          font-size: ${isMobile ? '12px' : '14px'};
          line-height: 1;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: ${isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))'};
          gap: ${isMobile ? '8px' : '20px'};
          padding: ${isMobile ? '12px 8px' : '30px'};
          background: #f8fafc;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: ${isMobile ? '12px' : '20px'};
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          text-align: center;
        }

        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: #6366f1;
        }

        .stat-card.total::before { background: #475569; }
        .stat-card.disponiveis::before { background: #10b981; }
        .stat-card.ocupadas::before { background: #ef4444; }
        .stat-card.trancadas::before { background: #f59e0b; }
        .stat-card.rascunhos::before { background: #3b82f6; }

        .stat-value {
          font-size: ${isMobile ? '20px' : '36px'};
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 4px;
          line-height: 1;
        }

        .stat-label {
          font-size: ${isMobile ? '10px' : '14px'};
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Estilos atualizados para tabs container com scroll */
        .tabs-container {
          display: flex;
          background: #f1f5f9;
          padding: 4px;
          border-radius: 12px;
          margin: ${isMobile ? '0 4px 12px' : '0 30px 30px'};
          overflow-x: auto;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }
        
        .tabs-container::-webkit-scrollbar {
          display: none;
        }

        .tab-item {
          flex: 0 0 auto;
          padding: ${isMobile ? '9px 10px' : '15px 20px'};
          border: none;
          background: transparent;
          color: #64748b;
          font-weight: 600;
          font-size: ${isMobile ? '11px' : '15px'};
          border-radius: 10px;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          min-width: ${isMobile ? '72px' : '110px'};
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }

        .tab-item:hover {
          color: #6366f1;
          background: rgba(99, 102, 241, 0.1);
        }

        .tab-item.active {
          background: white;
          color: #6366f1;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .tab-content {
          padding: ${isMobile ? '12px 8px' : '30px'};
          background: #f8fafc;
          border-radius: 16px;
          margin: ${isMobile ? '0 4px 12px' : '0 30px 30px'};
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          border: 1px solid #e2e8f0;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          min-width: 0;
        }

        .days-container {
          background: white;
          border-radius: 16px;
          padding: ${isMobile ? '12px' : '25px'};
          margin: ${isMobile ? '0 4px 12px' : '0 30px 20px'};
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
        }

        .days-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .days-scroll {
          display: flex;
          gap: ${isMobile ? '8px' : '15px'};
          overflow-x: auto;
          padding-bottom: 8px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        
        .days-scroll::-webkit-scrollbar {
          display: none;
        }

        .day-card {
          min-width: ${isMobile ? '80px' : '130px'};
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border: 2px solid transparent;
          border-radius: 12px;
          padding: ${isMobile ? '12px' : '20px'};
          cursor: pointer;
          transition: all 0.3s ease;
          flex-shrink: 0;
          text-align: center;
        }

        .day-card:hover {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          transform: translateY(-2px);
        }

        .day-card.active {
          background: white;
          border-color: #6366f1;
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.2);
        }

        .day-label {
          font-size: ${isMobile ? '11px' : '13px'};
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .day-date {
          font-size: ${isMobile ? '24px' : '32px'};
          font-weight: 800;
          color: #0f172a;
          margin: 6px 0;
        }

        .day-vagas-count {
          font-size: ${isMobile ? '11px' : '14px'};
          color: #64748b;
        }

        .horarios-section {
          background: white;
          border-radius: 16px;
          padding: ${isMobile ? '12px' : '25px'};
          margin: ${isMobile ? '0 4px 12px' : '0 30px 20px'};
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
        }

        .horarios-scroll {
          display: flex;
          gap: ${isMobile ? '8px' : '15px'};
          overflow-x: auto;
          padding-bottom: 8px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        
        .horarios-scroll::-webkit-scrollbar {
          display: none;
        }

        .horario-card {
          min-width: ${isMobile ? '70px' : '100px'};
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border: 2px solid transparent;
          border-radius: 12px;
          padding: ${isMobile ? '12px' : '20px'};
          cursor: pointer;
          transition: all 0.3s ease;
          flex-shrink: 0;
          text-align: center;
        }

        .horario-card:hover {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          transform: translateY(-2px);
        }

        .horario-card.active {
          background: white;
          border-color: #6366f1;
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.2);
        }

        .horario-time {
          font-size: ${isMobile ? '16px' : '20px'};
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 4px;
        }

        .horario-vagas-count {
          font-size: ${isMobile ? '11px' : '14px'};
          color: #64748b;
        }

        /* GRID DE VAGAS OTIMIZADO PARA MOBILE */
        .vagas-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))'};
          gap: ${isMobile ? '12px' : '20px'};
          padding: ${isMobile ? '12px 4px' : '30px'};
          background: #f8fafc;
        }

        .vaga-card {
          background: white;
          border-radius: 16px;
          padding: ${isMobile ? '16px' : '25px'};
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
        }

        .vaga-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.15);
        }

        .vaga-card.atualizando {
          animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .vaga-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: ${isMobile ? '12px' : '20px'};
          gap: 8px;
        }

        .vaga-time {
          font-size: ${isMobile ? '16px' : '18px'};
          font-weight: 650;
          color: #0f172a;
          background: linear-gradient(135deg, #0f5a30 0%, #0f5a30 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          flex-shrink: 0;
        }

        .vaga-status {
          padding: ${isMobile ? '6px 12px' : '8px 16px'};
          border-radius: 50px;
          font-size: ${isMobile ? '10px' : '11px'};
          font-weight: 650;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .vaga-status.disponivel {
          background: linear-gradient(130deg, #d1fae5 0%, #a7f3d0 100%);
          color: #10b981;
        }

        .vaga-status.ocupada {
          background: linear-gradient(130deg, #fee2e2 0%, #fecaca 100%);
          color: #ef4444;
        }

        .vaga-status.trancada {
          background: linear-gradient(130deg, #fef3c7 0%, #fde68a 100%);
          color: #f59e0b;
        }

        .vaga-status.rascunho {
          background: linear-gradient(130deg, #dbeafe 0%, #bfdbfe 100%);
          color: #3b82f6;
        }

        .vaga-title {
          font-size: ${isMobile ? '14px' : '16px'};
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 12px;
          line-height: 1.4;
          word-break: break-word;
        }

        .vaga-info {
          font-size: ${isMobile ? '11px' : '12px'};
          color: #64748b;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .vaga-actions {
          display: grid;
          grid-template-columns: ${isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
          gap: ${isMobile ? '8px' : '10px'};
          margin-top: 16px;
        }

        .btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: ${isMobile ? '10px 8px' : '12px'};
          border-radius: 10px;
          font-weight: 600;
          font-size: ${isMobile ? '11px' : '13px'};
          transition: all 0.3s ease;
          cursor: pointer;
          border: none;
          outline: none;
          position: relative;
          overflow: hidden;
          min-height: ${isMobile ? '36px' : 'auto'};
          text-align: center;
        }

        .btn-outline {
          background: transparent;
          border: 2px solid #cbd5e1;
          color: #334155;
        }

        .btn-outline:hover {
          border-color: #6366f1;
          color: #6366f1;
          background: rgba(99, 102, 241, 0.05);
        }

        .btn-warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
        }

        .btn-warning:hover {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
        }

        .btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .btn-success:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
        }

        .btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }

        .btn-danger:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
        }

        .btn-primary {
          background: linear-gradient(135deg, #d97706 0%, #d37d1e 100%);
          color: white;
        }

        .btn-primary:hover {
          background: linear-gradient(135deg, #b45309 0%, #a96318 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(217, 119, 6, 0.3);
        }

        .btn-info {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
        }

        .btn-info:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
        }

        .btn-purple {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          color: white;
        }

        .btn-purple:hover {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
        }

        .quick-actions {
          background: white;
          border-radius: 16px;
          padding: ${isMobile ? '12px' : '21px'};
          margin: ${isMobile ? '0 4px 12px' : '0 30px 20px'};
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
        }

        .quick-actions-grid {
          display: grid;
          grid-template-columns: ${isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))'};
          gap: ${isMobile ? '12px' : '13px'};
          margin-top: 12px;
        }

        .empty-state {
          text-align: center;
          padding: ${isMobile ? '40px 16px' : '60px 30px'};
          background: white;
          border-radius: 16px;
          margin: ${isMobile ? '12px 4px' : '30px'};
          border: 2px dashed #cbd5e1;
        }

        .empty-state-icon {
          font-size: ${isMobile ? '40px' : '64px'};
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .empty-state-title {
          font-size: ${isMobile ? '16px' : '24px'};
          font-weight: 700;
          color: '#334155';
          margin-bottom: '8px';
        }

        .empty-state-description {
          color: '#64748b';
          font-size: ${isMobile ? '13px' : '16px'};
          max-width: '500px';
          margin: '0 auto';
          line-height: 1.4;
        }

        /* Estilos adicionais para feedback visual */
        .btn-trancada {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          border: 2px solid #d97706;
        }
        
        .btn-trancada:hover {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
        }
        
        .btn-destrancada {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: 2px solid #059669;
        }
        
        .btn-destrancada:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
        }
        
        .vaga-status-badge {
          transition: all 0.3s ease;
        }
        
        @keyframes statusChange {
          0% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        .status-changing {
          animation: statusChange 0.5s ease;
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        
        .btn-loading {
          position: relative;
          color: transparent !important;
        }
        
        .btn-loading::after {
          content: '';
          position: absolute;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Ajustes para telas muito pequenas */
        @media (max-width: 360px) {
          .admin-panel {
            padding: 6px 3px;
          }
          
          .header-title {
            font-size: 14px; /* Ajuste adicional para telas muito pequenas */
          }
          
          .header-periodo {
            font-size: 11px;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          
          .navegacao-semanas {
            width: 100%;
            justify-content: space-between;
          }
          
          .btn-navegacao {
            padding: 5px 8px;
            font-size: 10px;
            min-width: 70px;
            height: 30px;
            gap: 3px;
          }
          
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
            padding: 10px 6px;
          }
          
          .stat-value {
            font-size: 18px;
          }
          
          .stat-label {
            font-size: 9px;
          }
          
          .tab-item {
            min-width: 80px;
            padding: 8px 10px;
            font-size: 11px;
          }
          
          .day-card {
            min-width: 70px;
            padding: 10px;
          }
          
          .day-date {
            font-size: 20px;
          }
          
          .horario-card {
            min-width: 60px;
            padding: 10px;
          }
          
          .vaga-card {
            padding: 14px;
          }
          
          .vaga-actions {
            grid-template-columns: 1fr;
            gap: 6px;
          }
          
          .btn {
            padding: 8px;
            font-size: 10px;
            min-height: 32px;
          }
        }

        @media (max-width: 280px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
          
          .header-title {
            font-size: 13px;
          }
          
          .btn-navegacao {
            padding: 4px 6px;
            font-size: 9px;
            min-width: 60px;
            height: 28px;
            gap: 2px;
          }
          
          .tab-item {
            min-width: 70px;
            padding: 6px 8px;
            font-size: 10px;
            gap: 4px;
          }
          
          .day-card {
            min-width: 60px;
            padding: 8px;
          }
          
          .horario-card {
            min-width: 50px;
            padding: 8px;
          }
          
          .vaga-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }

        /* Ajustes para tablets em modo retrato */
        @media (min-width: 481px) and (max-width: 768px) {
          .vagas-grid {
            grid-template-columns: repeat(2, 1fr);
            padding: 16px;
          }
          
          .vaga-actions {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .header-title {
            font-size: 20px; /* Tamanho intermediário para tablets */
          }
          
          .btn-navegacao {
            padding: 7px 12px;
            font-size: 12px;
            min-width: 85px;
            gap: 5px;
          }
        }

        /* Ajustes para orientação paisagem em mobile */
        @media (max-width: 896px) and (orientation: landscape) {
          .days-scroll,
          .horarios-scroll {
            max-height: 120px;
          }
          
          .vaga-actions {
            grid-template-columns: repeat(3, 1fr);
          }
          
          .header-periodo {
            flex-direction: row;
            align-items: center;
          }
          
          .navegacao-semanas {
            width: auto;
          }
        }
        
        /* Ajustes para desktop - CORRIGIDO SOBREPOSIÇÃO */
        @media (min-width: 1024px) {
          .header-title {
            font-size: 25px; /* Ajuste final para desktop */
          }
          
          .btn-navegacao {
            padding: 8px 18px; /* Mais padding horizontal */
            font-size: 13px;
            min-width: 105px; /* Mais largura para caber texto + ícone */
            height: 40px;
            gap: 8px; /* Mais espaço entre ícone e texto */
          }
          
          .btn-navegacao.hoje {
            min-width: 80px; /* Hoje precisa de menos espaço */
          }
          
          .btn-navegacao span {
            font-size: 14px; /* Ícones um pouco maiores */
          }
        }
        
        /* Ajustes para telas muito grandes */
        @media (min-width: 1440px) {
          .btn-navegacao {
            min-width: 110px; /* Ainda mais largura para telas grandes */
          }
        }
        `}
      </style>
      
      {notificacao.tipo && (
        <Toast
          type={notificacao.tipo}
          message={notificacao.mensagem}
          onClose={() => setNotificacao({ tipo: '', mensagem: '' })}
        />
      )}
      
      {/* Modal para Publicar com Datas */}
      <ModalPublicarDatas
        isOpen={mostrarModalPublicarDatas}
        onClose={() => setMostrarModalPublicarDatas(false)}
        onConfirm={handlePublicarComDatas}
        loading={loadingPublicarDatas}
        isMobile={isMobile}
      />
      
      {/* Modal para Gerar Agenda Semanal */}
      <ModalGerarAgenda
        isOpen={mostrarModalGerarAgenda}
        onClose={() => setMostrarModalGerarAgenda(false)}
        onConfirm={handleGerarAgenda}
        loading={loadingGerarAgenda}
        isMobile={isMobile}
        createApi={createApi}
      />
      
      <div className="admin-panel">
        <div className="admin-container">
          {/* Header */}
          <div className="admin-header">
            <div className="header-content">
              <div className="header-left">
                <div className="header-icon">
                  {Icons.Settings}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h1 className="header-title">Painel Administrativo</h1>
                  <div className="header-periodo">
                    <div className="periodo-texto">
                      <span>{Icons.Calendar}</span>
                      <span>Agenda Semanal: {periodo ? formatarPeriodoSemanal(periodo.inicio, new Date(periodo.fim.getTime() - 1)) : 'Carregando...'}</span>
                    </div>
                    <div className="navegacao-semanas">
                      <button className="btn-navegacao" onClick={handleSemanaAnterior}>
                        {Icons.ChevronLeft}
                        {isMobile ? '' : 'Anterior'}
                      </button>
                      <button className="btn-navegacao hoje" onClick={handleHoje}>
                        Hoje
                      </button>
                      <button className="btn-navegacao" onClick={handleSemanaProxima}>
                        {isMobile ? '' : 'Próxima'}
                        {Icons.ChevronRight}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Estatísticas */}
          <div className="stats-grid">
            <div className="stat-card total">
              <div className="stat-value">{estatisticas.total}</div>
              <div className="stat-label">Total Vagas</div>
            </div>
            <div className="stat-card disponiveis">
              <div className="stat-value">{estatisticas.fixas}</div>
              <div className="stat-label">Vagas Fixas</div>
            </div>
            <div className="stat-card trancadas">
              <div className="stat-value">{estatisticas.trancadas}</div>
              <div className="stat-label">Trancadas</div>
            </div>
            <div className="stat-card ocupadas">
              <div className="stat-value">{estatisticas.ocupadas ?? 0}</div>
              <div className="stat-label">Vagas Ocupadas</div>
            </div>
            <div className="stat-card rascunhos">
              <div className="stat-value">{estatisticas.livres}</div>
              <div className="stat-label">Vagas Livres</div>
            </div>
          </div>

          {/* Ações Rápidas */}
          <div className="quick-actions">
            <h3 style={{
              fontSize: isMobile ? '16px' : '20px',
              fontWeight: 700,
              color: '#0f172a',
              marginBottom: '4px'
            }}>
              <span style={{ marginRight: '8px' }}>{Icons.Zap}</span>
              Ações Rápidas
            </h3>
            {!isMobile && (
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                Fluxo recomendado: <strong>1. Gerar</strong> → <strong>2. Publicar</strong>
              </p>
            )}
            <div className="quick-actions-grid">
              {/* 1. Gerar Agenda */}
              <button
                onClick={() => setMostrarModalGerarAgenda(true)}
                className="btn btn-purple"
                disabled={loadingPublicar || loadingLimpar || loadingPublicarDatas || loadingGerarAgenda}
              >
                <span>{loadingGerarAgenda ? '⏳' : Icons.Sparkles}</span>
                {loadingGerarAgenda ? 'Gerando...' : '1. Gerar Agenda'}
              </button>

              {/* 2. Publicar Semana Visualizada */}
              <button
                onClick={handlePublicarAgendas}
                className="btn btn-success"
                disabled={loadingPublicar || loadingLimpar || loadingPublicarDatas || loadingGerarAgenda}
                title={`Publicar ${estatisticas.rascunhos} rascunho(s) da semana visualizada`}
              >
                <span>{loadingPublicar ? '⏳' : Icons.CalendarCheck}</span>
                {loadingPublicar
                  ? 'Publicando...'
                  : `2. Publicar${estatisticas.rascunhos > 0 ? ` (${estatisticas.rascunhos})` : ''}`}
              </button>

              {/* Publicar por período específico */}
              <button
                onClick={() => setMostrarModalPublicarDatas(true)}
                className="btn btn-info"
                disabled={loadingPublicar || loadingLimpar || loadingPublicarDatas || loadingGerarAgenda}
              >
                <span>{loadingPublicarDatas ? '⏳' : Icons.CalendarRange}</span>
                {loadingPublicarDatas ? 'Processando...' : 'Publicar por Período'}
              </button>

              {/* Recarregar */}
              <button onClick={onRefresh} className="btn btn-primary">
                <span>{Icons.Refresh}</span>
                Recarregar Vagas
              </button>

              {/* Limpar Agendas */}
              <button
                onClick={handleLimparAgendas}
                className="btn btn-danger"
                disabled={loadingPublicar || loadingLimpar || loadingPublicarDatas || loadingGerarAgenda}
              >
                <span>{loadingLimpar ? '⏳' : Icons.CalendarX}</span>
                {loadingLimpar ? 'Limpando...' : 'Limpar Semana'}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs-container">
            {abas.map(aba => (
              <button
                key={aba.id}
                onClick={() => setAbaAtiva(aba.id)}
                className={`tab-item ${abaAtiva === aba.id ? 'active' : ''}`}
              >
                <span>{aba.icon}</span>
                <span>{aba.label}</span>
              </button>
            ))}
          </div>

          {/* Conteúdo das Abas */}
          <div className="tab-content">
            {abaAtiva === 'vendas' && (
              <div>
                {/* Dias da Semana */}
                {dias.length > 0 && (
                  <div className="days-container">
                    <div className="days-header">
                      <h3 style={{ 
                        fontSize: isMobile ? '15px' : '20px', 
                        fontWeight: 700, 
                        color: '#0f172a' 
                      }}>
                        Dias da Semana
                      </h3>
                    </div>
                    <div className="days-scroll">
                      {dias.map(dia => (
                        <button
                          key={dia.data.getTime()}
                          onClick={() => {
                            setSelectedDayKey(getDiaKey(dia));
                            setSelectedHorario(null);
                          }}
                          className={`day-card ${selectedDayKey === getDiaKey(dia) ? 'active' : ''}`}
                        >
                          <div className="day-label">{dia.diaSemanaAbrev}</div>
                          <div className="day-date">{dia.diaNumero}</div>
                          <div className="day-vagas-count">
                            {dia.vagas.length} vagas
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Horários do Dia */}
                {selectedDay && horariosDoDia.length > 0 && (
                  <div className="horarios-section">
                    <div className="days-header">
                      <h3 style={{ 
                        fontSize: isMobile ? '15px' : '18px', 
                        fontWeight: 700, 
                        color: '#0f172a' 
                      }}>
                        Horários do Dia
                      </h3>
                      {selectedHorario && (
                        <button
                          onClick={() => setSelectedHorario(null)}
                          className="btn btn-outline"
                          style={{ padding: isMobile ? '8px 12px' : '10px 20px' }}
                        >
                          <span>{Icons.X}</span>
                          Limpar Filtro
                        </button>
                      )}
                    </div>
                    <div className="horarios-scroll">
                      {horariosDoDia.map(horario => (
                        <button
                          key={horario}
                          onClick={() => setSelectedHorario(selectedHorario === horario ? null : horario)}
                          className={`horario-card ${selectedHorario === horario ? 'active' : ''}`}
                        >
                          <div className="horario-time">{horario}</div>
                          <div className="horario-vagas-count">
                            {selectedDay.vagas.filter(v => formatarHora(v.data_hora) === horario).length} vagas
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vagas */}
                {selectedDay ? (
                  <div>
                    <div style={{
                      padding: isMobile ? '16px' : '30px',
                      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                      margin: isMobile ? '0 4px 12px' : '0 30px 20px',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '12px'
                    }}>
                      <div>
                        <h2 style={{
                          fontSize: isMobile ? '18px' : '28px',
                          fontWeight: 800,
                          color: '#0f172a',
                          marginBottom: '8px',
                          lineHeight: 1.2
                        }}>
                          {selectedDay.diaSemana}, {selectedDay.diaNumero}/{selectedDay.mesNumero}/{selectedDay.ano}
                          {selectedHorario && (
                            <span style={{ color: '#6366f1', marginLeft: '8px' }}>
                              • {selectedHorario}h
                            </span>
                          )}
                        </h2>
                        <p style={{
                          color: '#64748b',
                          fontSize: isMobile ? '13px' : '16px'
                        }}>
                          {vagasDoHorario.length} vagas disponíveis
                        </p>
                      </div>
                      <button
                        onClick={handleAtualizarVagas}
                        disabled={statusAtualizarVagas === 'atualizando'}
                        title="Recarregar vagas — use após editar, adicionar ou remover para ver a mudança refletida"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: isMobile ? '8px 12px' : '10px 16px',
                          borderRadius: '10px',
                          border: `1px solid ${statusAtualizarVagas === 'ok' ? '#16a34a' : '#cbd5e1'}`,
                          background: statusAtualizarVagas === 'ok' ? '#f0fdf4' : '#fff',
                          color: statusAtualizarVagas === 'ok' ? '#15803d' : '#334155',
                          fontWeight: 600,
                          fontSize: isMobile ? '13px' : '14px',
                          cursor: statusAtualizarVagas === 'atualizando' ? 'default' : 'pointer',
                          opacity: statusAtualizarVagas === 'atualizando' ? 0.7 : 1,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          transition: 'background 0.2s, border-color 0.2s, color 0.2s'
                        }}
                      >
                        <span style={{
                          display: 'inline-block',
                          animation: statusAtualizarVagas === 'atualizando' ? 'spin 0.7s linear infinite' : 'none'
                        }}>
                          {statusAtualizarVagas === 'ok' ? '✓' : '↻'}
                        </span>
                        {!isMobile && (
                          statusAtualizarVagas === 'atualizando' ? 'Atualizando...'
                          : statusAtualizarVagas === 'ok' ? 'Atualizado!'
                          : 'Atualizar'
                        )}
                      </button>
                    </div>

                    {vagasDoHorario.length > 0 ? (
                      <div className="vagas-grid">
                        {vagasDoHorario.map((vaga) => {
                          const statusInfo = getStatusInfo(vaga, vagasAtualizadasLocalmente);
                          const estaAtualizando = botoesAtualizando[vaga.id];
                          const acoesMap = mapAcaoParaBotao(vaga);
                          
                          return (
                            <div key={vaga.id} className={`vaga-card ${estaAtualizando ? 'atualizando' : ''}`}>
                              <div className="vaga-header">
                                <div className="vaga-time">
                                  {formatarHora(vaga.data_hora)}
                                </div>
                                <div className={`vaga-status ${statusInfo.cls} ${estaAtualizando ? 'status-changing' : ''}`}>
                                  <span>{statusInfo.icon}</span>
                                  {statusInfo.text}
                                </div>
                              </div>
                              
                              <div className="vaga-title">
                                Vaga {vaga.numero_vaga || '—'}
                              </div>
                              {vaga.descricao && (
                                <div className="vaga-info" style={{ fontStyle: 'italic', color: '#64748b' }}>
                                  <span>📝</span> {vaga.descricao}
                                </div>
                              )}
                              
                              {vaga.aluno_nome && (
                                <div className="vaga-info">
                                  <span>{Icons.Users}</span>
                                  <strong>Agendado:</strong> {vaga.aluno_nome}
                                </div>
                              )}

                              {vaga.aluno_fixo_nome && !vaga.aluno_nome && (
                                <div className="vaga-info">
                                  <span>⭐</span>
                                  <strong>Fixo:</strong> {vaga.aluno_fixo_nome}
                                </div>
                              )}

                              {vaga.secao && (
                                <div className="vaga-info">
                                  <strong>Seção:</strong> {vaga.secao}
                                </div>
                              )}
                              
                              <div className="vaga-actions">
                                {statusInfo.acoesPermitidas.map(acaoId => {
                                  const acao = acoesMap[acaoId];
                                  const estaAtualizandoEstaAcao = botoesAtualizando[vaga.id] === acaoId;
                                  return (
                                    <button
                                      key={acaoId}
                                      onClick={acao.handler}
                                      className={`btn ${acao.className} ${estaAtualizandoEstaAcao ? 'btn-loading' : ''}`}
                                      disabled={estaAtualizandoEstaAcao}
                                    >
                                      {!estaAtualizandoEstaAcao && <span>{acao.icon}</span>}
                                      <span>{acao.label}</span>
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Fila de espera - só mostra se o horário tem fila */}
                              {Number(vaga.fila_count) > 0 && (
                              <div style={{ marginTop: '8px' }}>
                                {filasPorVaga[vaga.id] === undefined ? (
                                  <button
                                    onClick={() => carregarFilaVaga(vaga.id)}
                                    disabled={filasCarregando[vaga.id]}
                                    style={{ background: 'none', border: '1px solid #ede9fe', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', color: '#7c3aed', cursor: 'pointer', width: '100%' }}
                                  >
                                    {filasCarregando[vaga.id] ? 'Carregando...' : '⏳ Ver fila de espera'}
                                  </button>
                                ) : filasPorVaga[vaga.id].length > 0 ? (
                                  <div style={{ backgroundColor: '#f5f3ff', borderRadius: '8px', padding: '8px', border: '1px solid #ede9fe' }}>
                                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#7c3aed', marginBottom: '6px' }}>
                                      ⏳ Fila ({filasPorVaga[vaga.id].length})
                                    </div>
                                    {filasPorVaga[vaga.id].map((entry, i) => (
                                      <div key={entry.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px', backgroundColor: 'white', borderRadius: '6px', marginBottom: i < filasPorVaga[vaga.id].length - 1 ? '4px' : 0 }}>
                                        <div style={{ fontSize: '11px', overflow: 'hidden' }}>
                                          <span style={{ fontWeight: '700', color: '#7c3aed', marginRight: '4px' }}>#{i + 1}</span>
                                          <span style={{ fontWeight: '600', color: '#1e293b' }}>{entry.nome}</span>
                                          <div style={{ fontSize: '10px', color: '#64748b' }}>{entry.email}</div>
                                        </div>
                                        <button
                                          onClick={() => removerDaFilaAdmin(entry.id, vaga.id)}
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '16px', padding: '0 4px', flexShrink: 0 }}
                                          title="Remover da fila"
                                        >✕</button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', padding: '4px' }}>Sem fila de espera</div>
                                )}
                              </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="empty-state">
                        <div className="empty-state-icon">
                          {selectedHorario ? '🕐' : '📭'}
                        </div>
                        <h3 className="empty-state-title">
                          {selectedHorario 
                            ? `Nenhuma vaga encontrada para ${selectedHorario}h`
                            : 'Nenhuma vaga encontrada para este dia'}
                        </h3>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      📅
                    </div>
                    <h3 className="empty-state-title">Selecione um dia</h3>
                    <p className="empty-state-description">
                      Escolha um dia da semana para visualizar as vagas disponíveis
                    </p>
                  </div>
                )}
              </div>
            )}

            {abaAtiva === 'frequencia' && (
              <ControleFrequencia
                createApi={createApi}
                isMobile={isMobile}
                periodo={periodo}
                dataReferencia={dataReferencia}
                setDataReferencia={setDataReferencia}
                onRefresh={onRefresh}
              />
            )}

            {abaAtiva === 'configuracoes' && (
              <div>
                {/* Sub-abas de Configurações */}
                <div style={{
                  display: 'flex', gap: '4px', padding: isMobile ? '10px 12px 0' : '14px 20px 0',
                  borderBottom: '1px solid #f1f5f9', overflowX: 'auto',
                  background: '#fafbfc'
                }}>
                  {[
                    { id: 'semana',       label: '📅 Config. da Semana' },
                    { id: 'agendamento',  label: '📋 Regras de Agendamento' },
                    { id: 'visualizacao', label: '👁️ Regras de Visualização' }
                  ].map(sub => (
                    <button key={sub.id} onClick={() => setSubAbaConfiguracoes(sub.id)} style={{
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      borderRadius: '10px 10px 0 0', border: 'none', cursor: 'pointer',
                      fontSize: isMobile ? '0.78rem' : '0.85rem', fontWeight: subAbaConfiguracoes === sub.id ? 700 : 500,
                      whiteSpace: 'nowrap', flexShrink: 0,
                      background: subAbaConfiguracoes === sub.id ? 'white' : 'transparent',
                      color: subAbaConfiguracoes === sub.id ? 'var(--color-primary, #0f5a30)' : '#64748b',
                      borderBottom: subAbaConfiguracoes === sub.id ? '2px solid var(--color-primary, #0f5a30)' : '2px solid transparent',
                      transition: 'all 0.15s ease'
                    }}>
                      {sub.label}
                    </button>
                  ))}
                </div>

                {subAbaConfiguracoes === 'semana' && (
                  <GerenciadorConfiguracoes createApi={createApi} isMobile={isMobile} />
                )}
                {subAbaConfiguracoes === 'agendamento' && (
                  <GerenciadorRegras createApi={createApi} isMobile={isMobile} />
                )}
                {subAbaConfiguracoes === 'visualizacao' && (
                  <GerenciadorVisualizacaoAgenda createApi={createApi} isMobile={isMobile} />
                )}
              </div>
            )}

            {abaAtiva === 'alunos' && (
              <div>
                <div style={{
                  display: 'flex', gap: '4px', padding: isMobile ? '10px 12px 0' : '14px 20px 0',
                  borderBottom: '1px solid #f1f5f9', overflowX: 'auto', background: '#fafbfc'
                }}>
                  {[
                    { id: 'usuarios',        label: '👤 Usuários' },
                    { id: 'alunosFixos',     label: '⭐ Horários Fixos' },
                    { id: 'acompanhamento',  label: '📊 Acompanhamento' },
                  ].map(sub => (
                    <button key={sub.id} onClick={() => setSubAbaAlunos(sub.id)} style={{
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      borderRadius: '10px 10px 0 0', border: 'none', cursor: 'pointer',
                      fontSize: isMobile ? '0.78rem' : '0.85rem', fontWeight: subAbaAlunos === sub.id ? 700 : 500,
                      whiteSpace: 'nowrap', flexShrink: 0,
                      background: subAbaAlunos === sub.id ? 'white' : 'transparent',
                      color: subAbaAlunos === sub.id ? 'var(--color-primary, #0f5a30)' : '#64748b',
                      borderBottom: subAbaAlunos === sub.id ? '2px solid var(--color-primary, #0f5a30)' : '2px solid transparent',
                      transition: 'all 0.15s ease'
                    }}>
                      {sub.label}
                    </button>
                  ))}
                </div>

                {subAbaAlunos === 'usuarios' && (
                  <GerenciadorUsuarios createApi={createApi} isMobile={isMobile} user={user} />
                )}
                {subAbaAlunos === 'alunosFixos' && (
                  <GerenciadorAlunosFixos createApi={createApi} isMobile={isMobile} />
                )}
                {subAbaAlunos === 'acompanhamento' && (
                  <AcompanhamentoAlunosFixos createApi={createApi} isMobile={isMobile} />
                )}
              </div>
            )}

            {abaAtiva === 'publicacao' && (
              <div>
                <div style={{
                  display: 'flex', gap: '4px', padding: isMobile ? '10px 12px 0' : '14px 20px 0',
                  borderBottom: '1px solid #f1f5f9', overflowX: 'auto', background: '#fafbfc'
                }}>
                  {[
                    { id: 'publicacao', label: '📤 Publicação' },
                    { id: 'google',     label: '📅 Google Agenda' },
                  ].map(sub => (
                    <button key={sub.id} onClick={() => setSubAbaPublicacao(sub.id)} style={{
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      borderRadius: '10px 10px 0 0', border: 'none', cursor: 'pointer',
                      fontSize: isMobile ? '0.78rem' : '0.85rem', fontWeight: subAbaPublicacao === sub.id ? 700 : 500,
                      whiteSpace: 'nowrap', flexShrink: 0,
                      background: subAbaPublicacao === sub.id ? 'white' : 'transparent',
                      color: subAbaPublicacao === sub.id ? 'var(--color-primary, #0f5a30)' : '#64748b',
                      borderBottom: subAbaPublicacao === sub.id ? '2px solid var(--color-primary, #0f5a30)' : '2px solid transparent',
                      transition: 'all 0.15s ease'
                    }}>
                      {sub.label}
                    </button>
                  ))}
                </div>

                {subAbaPublicacao === 'publicacao' && (
                  <AgendadorPublicacao createApi={createApi} isMobile={isMobile} />
                )}
                {subAbaPublicacao === 'google' && (
                  <GerenciadorAgendaGoogle createApi={createApi} isMobile={isMobile} />
                )}
              </div>
            )}

            {abaAtiva === 'financeiro' && (
              <div style={{ padding: isMobile ? '0' : '0' }}>
                <GestaoFinanceira createApi={createApi} isMobile={isMobile} />
              </div>
            )}

            {abaAtiva === 'sistema' && (
              <div>
                {/* Sub-abas de Sistema */}
                <div style={{
                  display: 'flex', gap: '4px', padding: isMobile ? '10px 12px 0' : '14px 20px 0',
                  borderBottom: '1px solid #f1f5f9', overflowX: 'auto',
                  background: '#fafbfc'
                }}>
                  {[
                    { id: 'personalizar',  label: '🎨 Personalização' },
                    { id: 'bot',           label: '🤖 Bot' },
                    { id: 'config',        label: '⚙️ Configurações' },
                    { id: 'integracoes',   label: '🔌 Integrações' },
                  ].map(sub => (
                    <button key={sub.id} onClick={() => setSubAbaSistema(sub.id)} style={{
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      borderRadius: '10px 10px 0 0', border: 'none', cursor: 'pointer',
                      fontSize: isMobile ? '0.78rem' : '0.85rem', fontWeight: subAbaSistema === sub.id ? 700 : 500,
                      whiteSpace: 'nowrap', flexShrink: 0,
                      background: subAbaSistema === sub.id ? 'white' : 'transparent',
                      color: subAbaSistema === sub.id ? 'var(--color-primary, #0f5a30)' : '#64748b',
                      borderBottom: subAbaSistema === sub.id ? '2px solid var(--color-primary, #0f5a30)' : '2px solid transparent',
                      transition: 'all 0.15s ease'
                    }}>
                      {sub.label}
                    </button>
                  ))}
                </div>

                {subAbaSistema === 'personalizar' && (
                  <GerenciadorPersonalizacao createApi={createApi} isMobile={isMobile} />
                )}
                {subAbaSistema === 'bot' && (
                  <GerenciadorBot createApi={createApi} isMobile={isMobile} />
                )}
                {subAbaSistema === 'config' && (
                  <GerenciadorSistema createApi={createApi} isMobile={isMobile} />
                )}
                {subAbaSistema === 'integracoes' && (
                  <GerenciadorIntegracoes createApi={createApi} isMobile={isMobile} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Edição */}
      {modalEditarVaga && (
        <ModalEditarVaga
          modalEditarVaga={modalEditarVaga}
          buscaAluno={buscaAluno}
          setBuscaAluno={setBuscaAluno}
          alunos={alunos}
          setAlunos={setAlunos}
          buscarAlunos={buscarAlunos}
          setModalEditarVaga={setModalEditarVaga}
          handleSalvarEdicaoVaga={handleSalvarEdicaoVaga}
          onLimparVaga={handleLimparVagaModal}
          loading={loading}
          isMobile={isMobile}
        />
      )}
    </>
  );
};
